import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import DashboardSidebar from "../components/DashboardSidebar";
import DashboardHeader from "../components/DashboardHeader";
import { calcReadiness, listRecentCompletions } from "../lib/progress";
import { TrendingUp, TrendingDown, BookOpen, Lightbulb, Check, Info } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------- Circular readiness ring ---------- */
const ReadinessRing = ({ value }) => {
  const size = 168;
  const stroke = 14;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (Math.max(0, Math.min(100, value)) / 100) * c;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#eef2f7" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="#1d4ed8"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`}
          fill="none"
          style={{ transition: "stroke-dasharray 500ms ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <p className="text-[36px] font-semibold text-[#1d4ed8] leading-none">{value}%</p>
        <p className="text-[12px] text-slate-500 mt-1">Interview Readiness</p>
      </div>
    </div>
  );
};

/* ---------- Maya scores line chart (SVG) ---------- */
const ScoresLineChart = ({ points }) => {
  const width = 640;
  const height = 220;
  const padL = 36;
  const padR = 16;
  const padT = 16;
  const padB = 28;

  if (!points || points.length === 0) return null;

  const w = width - padL - padR;
  const h = height - padT - padB;

  // X positions evenly spaced; Y from score_10 (0-10)
  const step = points.length > 1 ? w / (points.length - 1) : 0;
  const path = points
    .map((p, i) => {
      const x = padL + i * step;
      const y = padT + h - (p.score_10 / 10) * h;
      return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");

  // Area fill
  const areaPath =
    path +
    ` L ${(padL + (points.length - 1) * step).toFixed(1)} ${(padT + h).toFixed(1)} L ${padL.toFixed(
      1
    )} ${(padT + h).toFixed(1)} Z`;

  const yTicks = [0, 2, 4, 6, 8, 10];

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    } catch { return ""; }
  };

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto">
      <defs>
        <linearGradient id="scoresGrad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#1d4ed8" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#1d4ed8" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Y grid */}
      {yTicks.map((t) => {
        const y = padT + h - (t / 10) * h;
        return (
          <g key={t}>
            <line x1={padL} x2={width - padR} y1={y} y2={y} stroke="#eef2f7" strokeWidth="1" />
            <text x={padL - 8} y={y + 3} textAnchor="end" fontSize="10" fill="#94a3b8">
              {t}
            </text>
          </g>
        );
      })}

      {/* Area */}
      <path d={areaPath} fill="url(#scoresGrad)" />
      {/* Line */}
      <path d={path} stroke="#1d4ed8" strokeWidth="2.5" fill="none" strokeLinejoin="round" strokeLinecap="round" />
      {/* Dots */}
      {points.map((p, i) => {
        const x = padL + i * step;
        const y = padT + h - (p.score_10 / 10) * h;
        return (
          <g key={i}>
            <circle cx={x} cy={y} r="4" fill="#ffffff" stroke="#1d4ed8" strokeWidth="2" />
            <title>{`${fmtDate(p.date)} — ${p.score_10}/10`}</title>
          </g>
        );
      })}

      {/* X labels — first, middle, last if enough points */}
      {points.map((p, i) => {
        if (points.length <= 6 || i === 0 || i === points.length - 1 || i === Math.floor(points.length / 2)) {
          const x = padL + i * step;
          return (
            <text key={`x-${i}`} x={x} y={height - 6} textAnchor="middle" fontSize="10" fill="#94a3b8">
              {fmtDate(p.date)}
            </text>
          );
        }
        return null;
      })}
    </svg>
  );
};

/* ---------- Recent Activity ---------- */
const RecentRow = ({ item, onOpen }) => {
  const isTip = item.type === "tip";
  const Icon = isTip ? Lightbulb : BookOpen;
  const date = (() => {
    try {
      const d = new Date(item.at);
      const now = new Date();
      const diff = Math.floor((now - d) / (1000 * 60 * 60 * 24));
      if (diff <= 0) return "Today";
      if (diff === 1) return "Yesterday";
      if (diff < 7) return `${diff} days ago`;
      return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    } catch { return ""; }
  })();
  return (
    <button
      onClick={onOpen}
      className="w-full flex items-center justify-between py-3 text-left group"
      data-testid={`recent-item-${item.key}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded-xl bg-[#eaf1ff] text-[#1d4ed8] flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <p className="font-medium text-slate-900 text-[15px] truncate group-hover:text-[#1d4ed8]">
            {item.title}
          </p>
          <p className="text-[12px] text-slate-500 truncate">
            {isTip ? "Interview Tip" : item.guideTitle || "Study Resource"} · {date}
          </p>
        </div>
      </div>
      <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#15803d] bg-[#e6f7ee] border border-[#c1e9d0] px-2.5 py-1 rounded-full shrink-0">
        <Check className="w-3.5 h-3.5" /> Completed
      </span>
    </button>
  );
};

const Progress = () => {
  const navigate = useNavigate();
  const [guides, setGuides] = useState([]);
  const [tips, setTips] = useState([]);
  const [usage, setUsage] = useState({ used: 0, limit: 15 });
  const [progressData, setProgressData] = useState(null);
  const [meProgress, setMeProgress] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  const refreshRecent = () => setRecent(listRecentCompletions().slice(0, 10));

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [g, t, u, p, me] = await Promise.all([
          axios.get(`${API}/guides`),
          axios.get(`${API}/tips`),
          axios.get(`${API}/maya/usage`).catch(() => ({ data: { used: 0, limit: 15 } })),
          axios.get(`${API}/maya/progress`).catch(() => ({ data: null })),
          axios.get(`${API}/me/progress`).catch(() => ({ data: null })),
        ]);
        if (!mounted) return;
        setGuides(g.data.guides || []);
        setTips(t.data.tips || []);
        setUsage(u.data);
        setProgressData(p.data);
        setMeProgress(me.data);
      } catch (_) {} finally { if (mounted) setLoading(false); }
    })();
    refreshRecent();
    const onChange = () => refreshRecent();
    window.addEventListener("medily.progress.change", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      mounted = false;
      window.removeEventListener("medily.progress.change", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  // Interview Readiness comes from the SAME backend source as Dashboard (/api/me).
  const readiness = meProgress?.interview_readiness ?? 0;

  const skillRows = useMemo(() => {
    if (!progressData?.skill_averages) return [];
    // Order: Communication, Ethics, Data Interpretation, Empathy, Clinical Reasoning
    const order = ["communication", "knowledge", "professionalism", "empathy", "structure"];
    return order.map((k) => ({ key: k, ...progressData.skill_averages[k] }));
  }, [progressData]);

  const hasMayaData = (progressData?.completed_stations || 0) > 0;

  return (
    <div className="min-h-screen bg-[#f5f8fd] flex">
      <DashboardSidebar />
      <div className="flex-1 min-w-0">
        <DashboardHeader />
        <main className="p-6 lg:p-10 space-y-6">
          <div>
            <p className="text-[28px] font-semibold text-slate-900">Your Progress</p>
            <p className="text-slate-500 text-[14px]">Track your growth and identify areas to focus on.</p>
          </div>

          {/* Top three cards */}
          <div className="grid lg:grid-cols-[1.2fr_1fr_1fr] gap-6">
            {/* Interview Readiness */}
            <div
              className="rounded-2xl bg-white border border-slate-100 p-6 shadow-sm flex items-center gap-6"
              data-testid="progress-readiness-card"
            >
              <ReadinessRing value={readiness} />
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-slate-900 font-semibold">
                  Interview Readiness
                </div>
                <p className="text-sm text-slate-500 mt-1 max-w-xs">
                  {readiness === 0
                    ? "Complete your first resource, tip or Maya interview to start building your readiness."
                    : `You're making steady progress — aim for 90% before interview day.`}
                </p>
                <button
                  onClick={() => navigate("/maya")}
                  className="mt-4 px-5 py-2 rounded-full bg-[#1d4ed8] text-white text-sm font-medium hover:bg-[#1e40af] transition"
                  data-testid="progress-continue-btn"
                >
                  Continue Practising
                </button>
              </div>
            </div>

            {/* Your Strengths */}
            <div
              className="rounded-2xl bg-white border border-slate-100 p-6 shadow-sm flex flex-col"
              data-testid="progress-strengths-card"
            >
              <div className="flex items-center gap-2 text-[#15803d]">
                <TrendingUp className="w-4 h-4" />
                <span className="text-sm font-medium">Your Biggest Strength</span>
              </div>
              {hasMayaData && progressData.strongest_skill ? (
                <>
                  <p className="mt-3 text-[28px] font-semibold text-slate-900 leading-tight">
                    {progressData.strongest_skill}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    Your highest-scoring area across Maya stations.
                  </p>
                </>
              ) : (
                <>
                  <p className="mt-3 text-[20px] font-semibold text-slate-400 leading-tight">
                    No data yet
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    Complete a Maya interview and we’ll surface your strongest skill.
                  </p>
                </>
              )}
            </div>

            {/* Your Weaknesses */}
            <div
              className="rounded-2xl bg-white border border-slate-100 p-6 shadow-sm flex flex-col"
              data-testid="progress-weaknesses-card"
            >
              <div className="flex items-center gap-2 text-[#b45309]">
                <TrendingDown className="w-4 h-4" />
                <span className="text-sm font-medium">Your Biggest Weakness</span>
              </div>
              {hasMayaData && progressData.weakest_skill ? (
                <>
                  <p className="mt-3 text-[28px] font-semibold text-slate-900 leading-tight">
                    {progressData.weakest_skill}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    Focus your next few sessions here for the biggest gains.
                  </p>
                </>
              ) : (
                <>
                  <p className="mt-3 text-[20px] font-semibold text-slate-400 leading-tight">
                    No data yet
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    We’ll pinpoint areas to focus on once you’ve completed a Maya station.
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Maya Scores Over Time + Skill Breakdown */}
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="rounded-2xl bg-white border border-slate-100 p-6 shadow-sm" data-testid="progress-scores-chart">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-slate-900">Maya Scores Over Time</p>
                {hasMayaData && (
                  <span className="text-[12px] text-slate-500">
                    Avg {progressData.overall_avg_10}/10 · {progressData.completed_stations} stations
                  </span>
                )}
              </div>
              <div className="mt-4 min-h-[220px] flex items-center justify-center">
                {hasMayaData ? (
                  <ScoresLineChart points={progressData.scores_over_time} />
                ) : (
                  <div className="text-center py-10">
                    <div className="w-14 h-14 mx-auto rounded-full bg-[#eef4ff] text-[#1d4ed8] flex items-center justify-center">
                      <TrendingUp className="w-6 h-6" />
                    </div>
                    <p className="mt-3 font-medium text-slate-900">No interview scores yet</p>
                    <p className="text-sm text-slate-500 mt-1 max-w-xs mx-auto">
                      Complete your first Maya station and your score trajectory will appear here.
                    </p>
                    <button
                      onClick={() => navigate("/maya")}
                      className="mt-4 px-5 py-2 rounded-full bg-[#1d4ed8] text-white text-sm font-medium hover:bg-[#1e40af]"
                    >
                      Start with Maya
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-2xl bg-white border border-slate-100 p-6 shadow-sm" data-testid="progress-skill-breakdown">
              <p className="font-semibold text-slate-900">Skill Breakdown</p>
              <div className="mt-5 space-y-4">
                {skillRows.map((s) => (
                  <div key={s.key}>
                    <div className="flex items-center justify-between text-[13px]">
                      <span className="text-slate-700 font-medium">{s.label}</span>
                      <span className="text-slate-500">
                        {s.sample_size ? `${s.value_10}/10` : "—"}
                      </span>
                    </div>
                    <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full bg-[#1d4ed8] rounded-full transition-all duration-500"
                        style={{ width: `${s.value_pct || 0}%` }}
                      />
                    </div>
                  </div>
                ))}
                {!hasMayaData && (
                  <p className="text-[13px] text-slate-500 text-center pt-2">
                    Skill scores will populate as you complete Maya stations.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="rounded-2xl bg-white border border-slate-100 p-6 shadow-sm" data-testid="progress-recent-activity">
            <div className="flex items-center justify-between mb-2">
              <p className="font-semibold text-slate-900">Recent Activity</p>
              {recent.length > 0 && (
                <button
                  onClick={() => navigate("/resources")}
                  className="text-[13px] font-medium text-[#1d4ed8] hover:underline"
                >
                  View all
                </button>
              )}
            </div>
            {recent.length === 0 ? (
              <div className="text-center py-10">
                <div className="w-12 h-12 mx-auto rounded-full bg-[#eef4ff] text-[#1d4ed8] flex items-center justify-center">
                  <Check className="w-5 h-5" />
                </div>
                <p className="mt-3 font-medium text-slate-900">Nothing completed yet</p>
                <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
                  Your completed study resources and interview tips will appear here so you can revisit them.
                </p>
                <div className="mt-4 flex items-center justify-center gap-2">
                  <button
                    onClick={() => navigate("/resources")}
                    className="px-4 py-2 rounded-full bg-[#1d4ed8] text-white text-sm font-medium hover:bg-[#1e40af]"
                  >
                    Browse Study Resources
                  </button>
                  <button
                    onClick={() => navigate("/dashboard/interviews")}
                    className="px-4 py-2 rounded-full border border-slate-200 text-slate-700 text-sm font-medium hover:border-[#1d4ed8] hover:text-[#1d4ed8]"
                  >
                    Interview Tips
                  </button>
                </div>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {recent.map((item) => (
                  <RecentRow key={item.key} item={item} onOpen={() => item.href && navigate(item.href)} />
                ))}
              </div>
            )}
          </div>

          {loading && (
            <div className="text-center text-slate-400 text-sm">Loading your progress…</div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Progress;
